import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import {
  fetchGetOzonInfo,
  fetchGetWarehouseTypes,
  fetchOrderExcludeChange,
  fetchPostOzonFbo,
  fetchUpdateOrder,
} from '../../../actions/orders';

import InputWrapper from '../../common/InputWrapper';
import Checkbox from '../../common/Checkbox';
import { fetchGetHierarchy } from '../../../actions/admin';
import Toogle from '../../common/Toogle';
import HierarchyItem from '../HierarchyItem/HierarchyItem';
import OzonBlocks from './OzonBlocks';
import { useAutoTranslation } from '../../../helpers/useAutoTranslation';

const Wrapper = styled('div')`
  padding-left: 0;
  padding-right: 0;
  box-shadow: none;
  border: 1px solid #ebebeb;
  > div {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    padding-bottom: 10px;
    &:last-child {
      padding-bottom: 0;
    }
  }
  .copy-tracking-number {
    position: relative;
    margin-top: -10px;
    padding-bottom: 16px !important;
    .fe-copy {
      position: absolute;
      top: 20px;
      right: 37px;
      width: 12px;
      height: 14px;
      cursor: pointer;
    }
    input {
      padding-right: 35px;
    }
  }

  .files-block {
    > div {
      display: flex;
      align-items: center;
      margin-top: 8px;
      &:first-child {
        margin-top: 0;
      }
      .file-name {
        display: block;
        max-width: 50%;
        color: #4fc6e1;
        font-size: 13px;
        text-decoration: underline;
        margin-right: 10px;
        text-overflow: ellipsis;
        overflow: hidden;
      }
      .dashed-line {
        flex: 1;
        border-top: 1px dashed #eceef0;
        margin-right: 10px;
      }
      .fe-printer {
        display: block;
        color: #98a6ad;
        font-size: 15px;
        margin-right: 5px;
      }
      .file-size {
        color: #98a6ad;
        font-size: 11px;
      }
    }
  }

  .card-title {
    padding-top: 3px;
    padding-bottom: 0;
    align-items: center;
    .header-title {
      margin: 0;
      font-size: 16px;
      padding-top: 3px;
      padding-bottom: 26px;
    }
  }
  .exclude-from-summary {
    min-height: 45px;
  }

  .utm-tag {
    padding-bottom: 10px;
    .utm-tag-name {
      color: #72747b;
      margin-bottom: 5px;
    }

    .utm-tag-value {
      display: flex;

      .utm-tag-string {
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin: 0 -13px 0 0;
        transition: 0.15s;
      }

      .fe-copy {
        align-self: center;
        cursor: pointer;
        opacity: 0;
        transition: 0.15s;
      }

      &:hover {
        .utm-tag-string {
          max-width: calc(100% - 21px);
          margin: 0 8px 0 0;
        }

        .fe-copy {
          opacity: 1;
        }
      }
    }
  }

  .admin-color {
    color: #f245d6;
  }
`;

const Children = styled('div')`
  margin: 0 0 0 16px;
`;

const MoreBlock = (props) => {
  const { currentOrder, handleEditOrder, fbmType, isCopy, setIsCopy, actualizeOrder } = props;
  const dispatch = useDispatch();

  const { t } = useAutoTranslation();

  const account = useSelector((state) => state.auth.account);

  const [isFlagSummaryRequesting, setIsFlagSummaryRequesting] = useState(false);
  const [isExcludeFromSummary, setIsExcludeFromSummary] = useState();

  const [isFlagStickerEacRequesting, setIsFlagStickerEacRequesting] = useState(false);
  const [isStickerEac, setIsStickerEac] = useState();

  const [documents, setDocuments] = useState(null);
  const adminPanel = localStorage.getItem('adminPanel');

  useEffect(() => {
    if (currentOrder.id && adminPanel === 'true') {
      dispatch(fetchGetHierarchy(currentOrder.id)).then((data) => {
        setDocuments(data.order);
      });
    }
  }, [currentOrder.id, adminPanel]);

  useEffect(() => {
    setIsExcludeFromSummary(currentOrder?.excludeFromSummary);
  }, [currentOrder?.excludeFromSummary]);

  useEffect(() => {
    setIsStickerEac(currentOrder?.stickerEAC);
  }, [currentOrder?.stickerEAC]);

  const currentPermission = currentOrder.permissionList?.fieldPermissions?.find(
    (f) => f.type === 'WAREHOUSE_EDITABLE',
  );
  const editable = currentPermission ? currentPermission.value : true;

  const handleChangeB2bFlag = (checked) => {
    handleEditOrder({
      ...currentOrder,
      b2bFlag: checked,
    });
  };

  const handleExcludeSummaryFlag = async (checked) => {
    setIsExcludeFromSummary(checked);
    setIsFlagSummaryRequesting(true);

    const requestBody = {
      id: currentOrder.id,
      exclude: checked,
    };

    const excludeFlagResult = await dispatch(fetchOrderExcludeChange(requestBody))
      .catch((error) =>
        toast.error(
          `${error.errors.message || 'Ошибка при переключении параметра "Не учитывать в сводке" '}`,
        ),
      )
      .finally(() => setIsFlagSummaryRequesting(false));

    if (excludeFlagResult.success) {
      toast.info('Параметр "Не учитывать в сводке" успешно изменен!');
      setIsFlagSummaryRequesting(false);
    }
  };

  const handleEacCertificationType = async (checked) => {
    setIsStickerEac(checked);
    setIsFlagStickerEacRequesting(true);

    const requestBody = {
      id: currentOrder.id,
      stickerEAC: checked,
    };

    const eacFlagResult = await dispatch(fetchUpdateOrder(requestBody))
      .catch((error) =>
        toast.error(
          `${error.errors.message || 'Ошибка при переключении параметра "Тип сертификации ЕАС" '}`,
        ),
      )
      .finally(() => setIsFlagStickerEacRequesting(false));

    if (eacFlagResult) {
      toast.info('Параметр "Тип сертификации ЕАС" успешно изменен!');
      setIsFlagStickerEacRequesting(false);
    }
  };

  useEffect(() => {
    if (currentOrder.isFboAuto === true) {
      dispatch(fetchGetOzonInfo(currentOrder.id));
    }
  }, [currentOrder.isFboAuto]);

  return (
    <>
      <Wrapper className={`card-box`}>
        <div style={{ display: 'flex' }} className="card-title border-bottom">
          <h4 className="header-title">{t('Дополнительно')}</h4>
        </div>

        {currentOrder.shortUrl ? (
          <>
            <div className="card-content mt-3" style={{ display: 'flex', alignItems: 'center' }}>
              <div className="block-subtitle">{t('Ссылка на заказ')}</div>
            </div>
            <div className="copy-tracking-number card-content">
              <InputWrapper value={currentOrder.shortUrl} readOnly />
              <CopyToClipboard
                text={currentOrder.shortUrl}
                onCopy={() => toast.info(t('Номер трекинга скопирован'))}
              >
                <i className="fe-copy" />
              </CopyToClipboard>
            </div>
          </>
        ) : null}

        {account.b2bFlagAllowed ? (
          <div className="mt-2">
            <Checkbox
              label={t('B2B заказ')}
              id={'b2bOrder'}
              checked={currentOrder?.b2bFlag}
              onChange={(e) => handleChangeB2bFlag(e.target.checked)}
              disabled={!editable}
            />
          </div>
        ) : null}

        <div
          className="mt-1 d-flex exclude-from-summary"
          style={{ flexDirection: 'column', rowGap: '12px' }}
        >
          <Checkbox
            label={t('Не учитывать в сводке')}
            id={'excludeFromSummary'}
            checked={isExcludeFromSummary}
            onChange={(e) => handleExcludeSummaryFlag(e.target.checked)}
          />
          {isFlagSummaryRequesting ? (
            <div
              className="spinner-border spinner-border-standalone text-primary ml-2"
              role="status"
            />
          ) : null}
          <Checkbox
            label={t('ЕАС стикер товара')}
            id={'stickerEAC'}
            checked={isStickerEac}
            onChange={(e) => handleEacCertificationType(e.target.checked)} //TODO: доделать
          />
          {isFlagStickerEacRequesting ? (
            <div
              className="spinner-border spinner-border-standalone text-primary ml-2"
              role="status"
            />
          ) : null}
        </div>

        {currentOrder.urlParams ? (
          <div className="mt-2 pb-0">
            <div className="block-subtitle">{t('UTM метки')}</div>
            {currentOrder.urlParams.map((parameter) => (
              <div className="utm-tag">
                <h5 className="utm-tag-name">{parameter.name}</h5>
                <div className="utm-tag-value">
                  <p className="utm-tag-string" title={parameter.value}>
                    {parameter.value}
                  </p>
                  <CopyToClipboard
                    text={parameter.value}
                    onCopy={() => toast.info(t('Номер метки скопирован'))}
                  >
                    <i className="fe-copy" />
                  </CopyToClipboard>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </Wrapper>
      {documents !== null && adminPanel === 'true' && (
        <Wrapper className="card-box">
          <div style={{ display: 'flex' }} className="card-title border-bottom">
            <h4 className="header-title admin-color">{t('Иерархия документов')}</h4>
          </div>
          <div style={{ paddingTop: '8px' }}>
            <HierarchyItem
              activated={documents.activated}
              docType={documents.docType}
              guid={documents.guid}
              humanId={documents.humanId}
              itemsCount={documents.itemsCount}
              showChildren={false}
            />
            <Children>
              {documents.children?.map((item, index) => (
                <HierarchyItem
                  key={index}
                  activated={item.activated}
                  docType={item.docType}
                  guid={item.guid}
                  humanId={item.humanId}
                  itemsCount={item.itemsCount}
                  children={item.children}
                  showChildren={true}
                />
              ))}
            </Children>
          </div>
        </Wrapper>
      )}
      {currentOrder?.delivery?.vehicleInfo && (
        <Wrapper className="card-box">
          <div style={{ display: 'flex' }} className="card-title border-bottom">
            <h4 className="header-title">Транспортная информация</h4>
          </div>
          <div style={{ paddingTop: '24px' }}>
            <p className="block-subtitle">Водитель</p>
            <p>{currentOrder?.delivery?.vehicleInfo?.driver.fullName}</p>
            <p>{currentOrder?.delivery?.vehicleInfo?.driver.phoneNumber}</p>
          </div>
          <div style={{ paddingTop: '8px' }}>
            <p className="block-subtitle">Машина</p>
            <p>
              {currentOrder?.delivery?.vehicleInfo?.vehicle?.brand}{' '}
              {currentOrder?.delivery?.vehicleInfo?.vehicle?.plateNumber}
            </p>
          </div>
        </Wrapper>
      )}
      {fbmType && fbmType === 'OZON' && (
        <OzonBlocks
          permissionList={currentOrder.permissionList.actionPermissions}
          orderId={currentOrder.id}
          isFboAuto={currentOrder.isFboAuto}
          setIsCopy={setIsCopy}
          isCopy={isCopy}
          actualizeOrder={actualizeOrder}
        />
      )}
    </>
  );
};

export default MoreBlock;
